import React from 'react';

/**
 * Catches a render crash in its subtree and shows a short inline message instead of a blank page
 * (a React render error unmounts the whole root otherwise — the "blank page" lesson).
 *
 * Props:
 *   fallback  optional: a node, or (error, reset) => node
 *   label     optional: what crashed, for the default message ("The T&M pricing panel")
 *   onError   optional: (error, info) => void
 *
 * A crash saves nothing, but it does throw away what was typed on the crashed screen (anything
 * saved before it stays saved): the default message says so.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // console.error is captured by utils/errorCollector (the feedback button's recent errors).
    console.error('ErrorBoundary caught a render error:', error, info && info.componentStack);
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (e) { /* never let the reporter crash the fallback */ }
    }
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { fallback, label } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;
    const what = label || 'This part of the page';
    const msg = (error && error.message) || String(error);
    return (
      <div className="alert alert-danger my-2" role="alert">
        {what} hit an error: {msg}. Unsaved changes on this screen were lost.
        <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={this.reset}>
          Try again
        </button>
      </div>
    );
  }
}
